import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface AnimatedStatCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon?: React.ReactNode;
  color?: string;
  delay?: number;
}

const AnimatedStatCard: React.FC<AnimatedStatCardProps> = ({
  title,
  value,
  change,
  changeLabel = 'vs last period',
  icon,
  color = '#3B82F6',
  delay = 0,
}) => {
  const getTrendIcon = () => {
    if (!change) return <Minus size={16} />;
    if (change > 0) return <TrendingUp size={16} />;
    return <TrendingDown size={16} />;
  };

  const getTrendColor = () => {
    if (!change) return 'text-gray-500';
    if (change > 0) return 'text-green-500';
    return 'text-red-500';
  };

  return (
    <motion.div
      className="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 overflow-hidden group hover:shadow-2xl transition-shadow duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      whileHover={{ y: -5 }}
    >
      {/* Background Gradient */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
        style={{
          background: `linear-gradient(135deg, ${color}22 0%, ${color}00 100%)`,
        }}
      />

      {/* Icon */}
      {icon && (
        <motion.div
          className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
          style={{ backgroundColor: `${color}15`, color }}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: delay + 0.2, type: 'spring', stiffness: 200 }}
        >
          {icon}
        </motion.div>
      )}

      {/* Title */}
      <motion.div
        className="text-sm font-medium text-gray-600 mb-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: delay + 0.3 }}
      >
        {title}
      </motion.div>

      {/* Value */}
      <motion.div
        className="text-3xl font-bold text-gray-900 mb-2"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: delay + 0.4, type: 'spring', stiffness: 100 }}
      >
        {value}
      </motion.div>

      {/* Change Indicator */}
      {change !== undefined && (
        <motion.div
          className={`flex items-center gap-1 text-sm ${getTrendColor()}`}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: delay + 0.5 }}
        >
          {getTrendIcon()}
          <span className="font-semibold">
            {change > 0 ? '+' : ''}{change.toFixed(1)}%
          </span>
          <span className="text-gray-500 text-xs ml-1">{changeLabel}</span>
        </motion.div>
      )}

      {/* Animated Border */}
      <motion.div
        className="absolute bottom-0 left-0 h-1 rounded-full"
        style={{ backgroundColor: color }}
        initial={{ width: 0 }}
        animate={{ width: '100%' }}
        transition={{ delay: delay + 0.6, duration: 0.8, ease: 'easeOut' }}
      />
    </motion.div>
  );
};

export default AnimatedStatCard;

